import React, { useEffect, useRef } from 'react';
import { motion } from 'motion/react';
import { Award, ArrowRight, Lock } from 'lucide-react';
import { useSettings } from './SettingsContext';
import { LEVEL_UP_COINS } from '../lib/constants';

interface LevelUpModalProps {
  level: number;
  onClose: () => void;
  isAnonymous?: boolean;
  onOpenAuth?: () => void;
  onClaimReward?: (coins: number) => void;
}

export default function LevelUpModal({ level, onClose, isAnonymous, onOpenAuth, onClaimReward }: LevelUpModalProps) {
  const { playSfx, triggerHaptic } = useSettings();

  const coinReward = LEVEL_UP_COINS[level] ?? 10;

  // Claim level-up reward only once on mount — do NOT re-fire when onClaimReward reference changes
  // (App re-renders frequently in multiplayer, recreating the onClaimReward callback each time)
  const hasClaimedRef = useRef(false);
  useEffect(() => {
    if (hasClaimedRef.current) return;
    hasClaimedRef.current = true;
    // Play celebratory sound and haptics on mount
    playSfx('levelUp');
    triggerHaptic('success');
    // Claim level-up coin reward
    if (onClaimReward) {
      onClaimReward(coinReward);
    }
  }, []); // Empty deps — intentionally run once only

  return (
    <div className="fixed inset-0 bg-black/85 backdrop-blur-xl z-[100] overflow-y-auto">
      {/* Decorative ambient lights */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-72 h-72 bg-yellow-500/10 rounded-full blur-[100px] pointer-events-none" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-amber-500/5 rounded-full blur-[120px] pointer-events-none" />

      {/* Floating particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(12)].map((_, i) => {
          const size = Math.random() * 6 + 4;
          const delay = Math.random() * 2;
          const duration = Math.random() * 3 + 3;
          const left = Math.random() * 100;
          return (
            <motion.div
              key={i}
              className="absolute bg-yellow-400/20 rounded-full"
              style={{
                width: size,
                height: size,
                left: `${left}%`,
                bottom: '-20px'
              }}
              animate={{
                y: '-105vh',
                x: [0, Math.random() * 40 - 20, 0],
                opacity: [0, 0.8, 0]
              }}
              transition={{
                duration,
                delay,
                repeat: Infinity,
                ease: 'easeOut'
              }}
            />
          );
        })}
      </div>

      {/* Main Container */}
      <div className="min-h-full flex items-center justify-center p-4">
      <motion.div
        initial={{ scale: 0.85, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.85, opacity: 0 }}
        transition={{ type: 'spring', stiffness: 200, damping: 20 }}
        className="w-full max-w-sm glass-panel rounded-3xl p-6 sm:p-8 text-center border border-yellow-500/25 relative overflow-hidden flex flex-col items-center justify-center shadow-[0_0_50px_rgba(234,179,8,0.15)] my-8"
      >
        {/* Shine highlight */}
        <div className="absolute -top-1/2 left-0 right-0 h-[200%] bg-gradient-to-b from-white/5 to-transparent rotate-45 pointer-events-none" />

        {/* Celebration Header */}
        <motion.div
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="space-y-1"
        >
          <span className="text-[10px] sm:text-xs uppercase tracking-[0.3em] font-black text-yellow-400/80 animate-pulse">Nuovo Traguardo!</span>
          <h2 className="text-3xl sm:text-4xl font-black uppercase text-white tracking-wide" style={{ fontFamily: '"Playfair Display", serif' }}>
            Level Up! 🎉
          </h2>
        </motion.div>

        {/* Animated Badge */}
        <div className="relative my-8 sm:my-10">
          {/* Pulsing ring */}
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: [1, 1.15, 1], opacity: [0.3, 0.6, 0.3] }}
            transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
            className="absolute inset-0 bg-yellow-500/20 rounded-full blur-md"
          />

          {/* Golden Badge Container */}
          <motion.div
            initial={{ scale: 0, rotate: -30 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ type: 'spring', stiffness: 180, damping: 15, delay: 0.4 }}
            className="w-28 h-28 sm:w-32 sm:h-32 rounded-full border-4 border-yellow-400 bg-gradient-to-b from-yellow-500 to-amber-600 flex flex-col items-center justify-center shadow-[0_10px_25px_rgba(234,179,8,0.3)] relative z-10"
          >
            <Award className="w-8 h-8 sm:w-10 sm:h-10 text-black mb-0.5" />
            <span className="text-[9px] uppercase font-black tracking-widest text-black/70 -mb-0.5">Livello</span>
            <span className="text-3xl sm:text-4xl font-black text-black leading-none">{level}</span>
          </motion.div>
        </div>

        {/* Comparison transition */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="flex items-center justify-center gap-4 text-sm font-semibold text-white/50 mb-6"
        >
          <span className="px-3 py-1 bg-white/5 rounded-full border border-white/5">Lvl {level - 1}</span>
          <ArrowRight className="w-4 h-4 text-yellow-400" />
          <span className="px-3 py-1 bg-yellow-500/10 text-yellow-400 rounded-full border border-yellow-500/20 font-bold">Lvl {level}</span>
        </motion.div>

        {/* Celebration text */}
        <motion.p
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.7 }}
          className="text-xs sm:text-sm text-white/60 leading-relaxed max-w-[260px] mb-8"
        >
          Fantastico! Hai raggiunto il **Livello {level}**. Continua a vincere partite e a scalare la classifica di Sbarazzo!
        </motion.p>

        {/* Action Button */}
        <motion.button
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.8 }}
          onClick={() => {
            playSfx('click');
            triggerHaptic('light');
            onClose();
          }}
          className="w-full py-4 bg-yellow-500 hover:bg-yellow-400 text-black font-black uppercase tracking-wider text-xs rounded-2xl transition-all hover:scale-[1.01] active:scale-[0.99] shadow-lg shadow-yellow-500/10 z-10 cursor-pointer"
        >
          Continua la scalata
        </motion.button>

        {isAnonymous && onOpenAuth && (
          <motion.button
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.9 }}
            onClick={() => {
              playSfx('click');
              triggerHaptic('medium');
              onOpenAuth();
            }}
            className="w-full py-3.5 mt-3 border border-yellow-500/30 hover:border-yellow-400 bg-yellow-500/10 hover:bg-yellow-500/20 text-yellow-400 font-bold uppercase tracking-wider text-xs rounded-2xl transition-all hover:scale-[1.01] active:scale-[0.99] flex items-center justify-center gap-2 cursor-pointer z-10 shadow-[0_0_15px_rgba(234,179,8,0.1)] animate-pulse"
          >
            <Lock className="w-3.5 h-3.5" />
            Salva Livello (Registrati Gratis)
          </motion.button>
        )}
      </motion.div>
      </div>
    </div>
  );
}
